/**
 * Types for app_logs table.
 * These types correspond to RLS policy constraints in the database.
 * 
 * RLS policy allows INSERT only when:
 * - source IN ('client', 'edge', 'rpc')
 * - level IN ('debug', 'info', 'warn', 'error', 'critical')
 */

export const ALLOWED_APP_LOG_SOURCES = ['client', 'edge', 'rpc'] as const;
export type AppLogSource = typeof ALLOWED_APP_LOG_SOURCES[number];

export const ALLOWED_APP_LOG_LEVELS = ['debug', 'info', 'warn', 'error', 'critical'] as const;
export type AppLogLevel = typeof ALLOWED_APP_LOG_LEVELS[number];

/**
 * Validates source value against RLS whitelist.
 * Falls back to 'client' for invalid values with dev warning.
 */
export function validateAppLogSource(source: string): AppLogSource {
  if (ALLOWED_APP_LOG_SOURCES.includes(source as AppLogSource)) {
    return source as AppLogSource;
  }
  if (import.meta.env.DEV) {
    console.warn(`[app-logs] Invalid source "${source}", falling back to "client". RLS requires: ${ALLOWED_APP_LOG_SOURCES.join(', ')}`);
  }
  return 'client';
}

/**
 * Validates level value against RLS whitelist.
 * Falls back to 'info' for invalid values with dev warning.
 */
export function validateAppLogLevel(level: string): AppLogLevel {
  const normalized = level.toLowerCase();
  if (ALLOWED_APP_LOG_LEVELS.includes(normalized as AppLogLevel)) {
    return normalized as AppLogLevel;
  }
  if (import.meta.env.DEV) {
    console.warn(`[app-logs] Invalid level "${level}", falling back to "info". RLS requires: ${ALLOWED_APP_LOG_LEVELS.join(', ')}`);
  }
  return 'info';
}
